import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { PatientGroup } from 'src/app/data/dietitian/domain/patient_group/patient_group';
import { Patient } from 'src/app/data/patient/domain/patient';
import { ConfirmationModalComponent } from 'src/app/ui/components/confirmation-modal/confirmation-modal.component';

import { PatientModel } from './patient.model';
import { PatientGroupPatientEditFacade } from './patient-group-patient-edit.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-patient-group-patient-edit',
  templateUrl: './patient-group-patient-edit.component.html',
  styleUrls: ['./patient-group-patient-edit.component.scss'],
  providers: [PatientGroupPatientEditFacade],
})
export class PatientGroupPatientEditComponent {
  private _patientGroup: PatientGroup | undefined;
  @Input() set patientGroup(value: PatientGroup | undefined) {
    if (value?.id != this._patientGroup?.id) {
      this.reset();
    }
    this._patientGroup = value;
    this.searchPatients(this.currentQuery).then();
  }
  get patientGroup(): PatientGroup | undefined {
    return this._patientGroup;
  }

  @Output() onClose = new EventEmitter();
  currentQuery?: string;
  currentPatientsOfGroup: Patient[] = [];
  patientIndex: { [key: string]: PatientModel } = {};
  visiblePatients: PatientModel[] = [];
  hasPendingChanges = false;
  isLoading = true;
  error?: string;
  bsModalRef?: BsModalRef;

  constructor(
    private facade: PatientGroupPatientEditFacade,
    private modalService: BsModalService,
  ) {
    // do nothing
  }

  reset() {
    this.patientIndex = {};
    this.visiblePatients = [];
    this.currentQuery = undefined;
    this.hasPendingChanges = false;
  }

  async searchPatients(query?: string) {
    this.currentQuery = query;
    this.isLoading = true;
    this.error = undefined;
    try {
      let groupPatients: Patient[];
      if (this.patientGroup) {
        const results = await this.facade.searchPatients(
          this.patientGroup.dietitianId.id.toString(),
          query ?? '',
        );
        groupPatients = results.results;
        this.currentPatientsOfGroup = await this.facade.getPatientGroupPatients(
          this.patientGroup.dietitianId.id.toString(),
          this.patientGroup.id.toString(),
        );
      } else {
        groupPatients = [];
        this.currentPatientsOfGroup = [];
      }

      const patientModels = groupPatients.map((p) =>
        PatientModel.fromPatient(
          p,
          this.currentPatientsOfGroup
            .map((p) => p.id.toString())
            .indexOf(p.id.toString()) >= 0,
        ),
      );
      const newVisiblePatients = [];
      for (const p of patientModels) {
        if (!this.patientIndex[p.id]) {
          this.patientIndex[p.id] = p;
        } else {
          const oldP = this.patientIndex[p.id];
          // check if patient has been updated
          if (
            (p.updatedAt?.getTime() ?? 0) > (oldP.updatedAt?.getTime() ?? 0)
          ) {
            this.patientIndex[p.id] = p;
          }
        }
        newVisiblePatients.push(this.patientIndex[p.id]);
      }
      newVisiblePatients.sort(PatientModel.sort);
      this.visiblePatients = newVisiblePatients;
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  updateSearchQuery(query?: string) {
    return this.searchPatients(query);
  }

  toggleCheck(patientModel: PatientModel) {
    patientModel.checked = !patientModel.checked;
    this.hasPendingChanges = true;
    this.visiblePatients.sort(PatientModel.sort);
  }

  async preUpdatePatientGroup() {
    if (!this.patientGroup) return;

    const selectedIds = Object.values(this.patientIndex)
      .filter((p) => p.checked)
      .map((p) => p.id.toString());
    let newPatient = false;
    for (const selectedId of selectedIds) {
      if (
        this.currentPatientsOfGroup
          .map((p) => p.id.toString())
          .filter((id) => id === selectedId).length === 0
      ) {
        newPatient = true;
        break;
      }
    }

    if (
      newPatient &&
      Object.values(this.patientIndex).filter((p) => p.checked).length > 0
    ) {
      const initialState: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          title: `Profil & Trackers...`,
          subtitle: `Souhaitez-vous appliquer le profil de tracking du groupe <strong>${this.patientGroup.name}</strong> à ces patients ? Si votre réponse est "Non", leur profil actuel sera conservé.`,
          validateLabel: 'Oui',
          cancelLabel: 'Non',
        },
      };
      const confirm = this.modalService.show(
        ConfirmationModalComponent,
        initialState,
      );

      if (confirm.content instanceof ConfirmationModalComponent) {
        confirm.content.validate.subscribe(
          async () => await this.updatePatientGroup(true),
        );
        confirm.content.cancel.subscribe(
          async () => await this.updatePatientGroup(false),
        );
      }
    } else {
      this.updatePatientGroup(false).then();
    }
  }

  private async updatePatientGroup(updateTrackers: boolean) {
    if (!this.patientGroup) return;

    this.isLoading = true;
    this.error = undefined;
    try {
      await this.facade.updatePatientGroupPatients(
        this.patientGroup?.id.toString(),
        Object.values(this.patientIndex)
          .filter((p) => p.checked)
          .map((p) => p.id),
        updateTrackers,
      );
      this.hasPendingChanges = false;
      this.tryToClose();
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  tryToClose() {
    if (this.hasPendingChanges) {
      this.showCloseConfirmationModal();
    } else {
      this.reset();
      this.onClose.emit();
    }
  }

  showCloseConfirmationModal() {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Abandonner les modifications en cours ?',
        subtitle: 'Les changements ne seront pas enregistrés.',
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(() => {
        this.onClose.emit();
      });
    }
  }
}
